import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  getHistoryUiRevision,
  subscribeHistoryUiRevision,
} from '../../../utils/gridHistoryUi';
import { syncSelectionOutlineFromStore } from '../../../utils/ThreeJsUtils/selectionOutline';
import {
  hasActivePivotSession,
  getPivotSessionMarker,
} from '../../../utils/ThreeJsUtils/pivotEditor';
import { iconsPath } from '../../../utils/imagePath';
import * as Three from 'three';

import LearningCenter from '../../SupportMenu/LearningCenter';
import EventsComponent from '../../AddComponents/EventsComponent';
import AudioComponent from '../../AddComponents/AudioComponent';
import LightComponent from '../../AddComponents/LightComponent';
import VideoComponent from '../../AddComponents/VideoComponent';
import PhysicsComponent from '../../AddComponents/PhysicsComponent';
import EnvironmentComponent from '../../AddComponents/EnvironmentComponent';
import ParticleComponent from '../../AddComponents/ParticleComponent';
import CameraComponent from '../../AddComponents/CameraComponent';
import RightMenuShortcutKeys from '../../SupportMenu/RightMenuShortcutKey';
import useClickOutside from '../../UseClickOutside';
import SupportMenu from '../../SupportMenu/SupportMenu';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import {
  DeleteMesh,
  Publish,
  selectObjectInScene,
} from '../../../pages/Dashboard/GridPage';
import { scheduleSelectLightInScene } from '../../../utils/ThreeJsUtils/lightHelperUtils';
import store from '../../../redux/store';
import {
  lightDataUpdate,
  selected_object,
} from '../../../redux/Slice/gridSlice';
import TextComponent from '../../AddComponents/TextComponent';
import HotSpotComponent from '../../AddComponents/HotSpotComponent';
import ModalAdd from '../../ui/AddComponentModal';
import { DividerRight, PublishSpinner } from '../../../utils/gridUtils';
import { allComponentOptions, LightInitialData } from '../../../utils/constant';
import { SimpleModal } from '../../ui/NormalModal';
import FeedbackModal from '../../SupportMenu/Feedback';
import MaterialComponentNew from '../../AddComponents/MaterialComponentNew';
import {
  selectionMeshesShareSingleMaterial,
  shouldShowMaterialComponent,
} from '../../../utils/ThreeJsUtils/materialPropertyEditor';
import {
  setPolyCount,
  setSelectedLightId,
  updateActiveComponent,
  setEventsPinned,
} from '../../../redux/Slice/globalStates';
import { FixedValue } from '../../../utils/UtilsFunction';
import { addLight } from '../../../redux/Slice/gridSlice';
import { updateParticle } from '../../../redux/Slice/particleSlice';

import { cleanAllAnimationObjects } from '../../../redux/Slice/animationSlice';
import { setEnvironmentData } from '../../../redux/Slice/environmentSlice';
import {
  getPolyCount,
  debouncedGridTransformUpdate,
} from './GridPageFunctions';
import { useNavigate } from 'react-router-dom';
import { objectHasPhysicsData } from '../../../utils/Physics/physicsObjectMatch';
import {
  getEventsForSceneObject,
  hydrateSceneObjectEvents,
  isEventCapableSceneObject,
} from '../../../utils/eventAssetUtils';
import {
  isSceneCamera,
  isSceneCameraPoint,
  resolveSceneCameraFromPoint,
} from '../../../utils/ThreeJsUtils/sceneCameraUtils';
import { Grid } from 'ldrs/react';
import 'ldrs/react/Grid.css';
import { PiEyeThin, PiEyeSlashThin } from 'react-icons/pi';
import {
  sceneScaleToPanelScale,
  applyPanelScaleToTextObject,
} from '../../gridpagefunction/Text';
import { normalizeEditableNumericValue } from '../../../utils/transformInputUtils';

// Utility function for debouncing
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Optimized value formatting
// NEW
const formatValue = (value, precision = 2) => {
  const num = Number(value ?? 0);
  const fixed = num.toFixed(Math.max(precision, 4));
  return parseFloat(fixed).toString();
};

const getSelectedSceneCamera = (scene, selectedObject) => {
  if (!selectedObject) return null;
  if (isSceneCamera(selectedObject)) return selectedObject;
  if (isSceneCameraPoint(selectedObject)) {
    return resolveSceneCameraFromPoint(scene, selectedObject);
  }
  return null;
};

const isSelectedSceneCamera = (scene, selectedObject) =>
  Boolean(getSelectedSceneCamera(scene, selectedObject));

/** True when the object is a video asset or has an attached video texture. */
const objectHasVideoComponent = (obj) => {
  if (!obj) return false;
  if (obj.deleteType === 'video') return true;
  if (obj.video_texture || obj.userData?.video_texture) return true;
  if (obj.userData?.attachedVideoFile) return true;
  if (obj.videoElement && obj.deleteType !== 'audio') return true;
  const clip = obj.userData?.video_settings?.video_clip;
  return (
    typeof clip === 'string' &&
    clip.trim() !== '' &&
    clip !== 'Upload clip'
  );
};

// Matches TransformControls gizmo axis colors
const AXIS_LABEL_COLORS = {
  X: '#ff4a56',
  Y: '#00e6b0',
  Z: '#009cff',
};

const AppRightSection = React.memo(
  ({
    setPublished,
    polyCount,
    completeImageObject,
    setSelectedObjectState,
    completeObject,
    renderer,
    setName,
    showLightHelpers,
    isAnimationMinimized,
    animationHeight = 250,
    isAnimationResizing = false,
    onHdriSelected,
    onMinimizeChange,
    transformControls,
    camera,
    category,
    setCategory,
    setRedirectionCode,
  }) => {
    const { scene, selectedObject } = completeImageObject;
    const [isLocked, setIsLocked] = useState(true);

    const lowerCategory = (category || 'ar').toLowerCase();
    const isBetaCategory = [
      'ar',
      'vr',
      'mr',
      'xr',
      'surface',
      'world',
      'vps',
      'foot',
      'body',
      'face',
      'hand',
      'wrist',
      'torso',
    ].includes(lowerCategory);

    const [historyUiRevision, setHistoryUiRevision] =
      useState(getHistoryUiRevision);

    useEffect(() => subscribeHistoryUiRevision(setHistoryUiRevision), []);

    const { animationObjects } = useSelector((state) => state.animation);
    const environmentData = useSelector(
      (state) => state.environment?.environmentData || {}
    );
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [isPopupOpen, setIsPopupOpen] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [showMenu, setShowMenu] = useState(true);
    const [hoveredOption, setHoveredOption] = useState(null);
    const [isPublishing, setIsPublishing] = useState(false);
    const [isMinimized, setIsMinimized] = useState(true);
    const [isSelectionBoxHidden, setIsSelectionBoxHidden] = useState(
      transformControls ? !transformControls.showSelectionBox : false
    );
    const [showTooltip, setShowTooltip] = useState(false);
    const navigate = useNavigate();
    const [isEnvironmentVisible, setIsEnvironmentVisible] = useState(false); // State for Environment visibility
    const { projectId } = completeObject;
    const [isEditing, setIsEditing] = useState({
      position: false,
      rotation: false,
      scale: false,
    });
    const {
      assets_data,
      select_object,
      light_data,
      camera_properties,
      selectedMeshesForGrouping,
    } = useSelector((state) => state.assets_object);

    const isMultiSelect = (selectedMeshesForGrouping?.length ?? 0) > 1;
    const sharedMaterialSelection =
      isMultiSelect &&
      selectionMeshesShareSingleMaterial(selectedMeshesForGrouping);
    const blockMaterialPanel =
      isMultiSelect && !sharedMaterialSelection;

    const PER_OBJECT_PANELS = useMemo(
      () => [
        'Material',
        'Audio',
        'Video',
        'Text',
        'HotSpot',
        'Particle',
        'Physics',
      ],
      []
    );

    const { activeComponentsNew, isEventsPinned } = useSelector(
      (state) => state.global_state,
      shallowEqual
    );

    // Get physics objects from Redux to check if selected object has physics
    const { physicsObjects } = useSelector((state) => state.physics);

    const dispatch = useDispatch();
    const prevSelectedObjectIdRef = useRef(null);
    const prevVideoSelectionIdRef = useRef(null);
    const prevEventsPanelActionRef = useRef(null);

    // Track whether Environment was just added
    const [environmentJustAdded, setEnvironmentJustAdded] = useState(false);
    const [hiddenAutoComponents, setHiddenAutoComponents] = useState({}); // Track user-dismissed / hidden components

    // Show Environment when nothing is selected (or Environment asset / manually added).
    // Never show Environment while a scene camera is selected.
    useEffect(() => {
      if (hiddenAutoComponents.Environment || isMultiSelect) {
        setIsEnvironmentVisible(false);
        return;
      }

      const isCamera = Boolean(
        selectedObject && isSelectedSceneCamera(scene, selectedObject)
      );
      const isEnvironment = selectedObject?.deleteType === 'Environment';
      const nothingSelected = !selectedObject;

      // Camera selection always hides Environment (Camera panel is shown instead).
      if (isCamera) {
        setIsEnvironmentVisible(false);
        setEnvironmentJustAdded(false);
        if (activeComponentsNew.includes('Environment')) {
          dispatch(
            updateActiveComponent({ option: 'Environment', type: 'remove' })
          );
        }
        return;
      }

      const shouldShow =
        nothingSelected || isEnvironment || environmentJustAdded;

      if (shouldShow) {
        setIsEnvironmentVisible(true);
        if (isEnvironment) setEnvironmentJustAdded(true);
        if (!activeComponentsNew.includes('Environment')) {
          dispatch(
            updateActiveComponent({ option: 'Environment', type: 'add' })
          );
        }
      } else {
        setIsEnvironmentVisible(false);
        if (
          activeComponentsNew.includes('Environment') &&
          !environmentJustAdded
        ) {
          dispatch(
            updateActiveComponent({ option: 'Environment', type: 'remove' })
          );
        }
      }
    }, [
      selectedObject,
      environmentJustAdded,
      activeComponentsNew,
      dispatch,
      hiddenAutoComponents.Environment,
      isMultiSelect,
      scene,
    ]);

    useEffect(() => {
      if (transformControls) {
        transformControls.showSelectionBox = !isSelectionBoxHidden;
      }
      syncSelectionOutlineFromStore();
    }, [isSelectionBoxHidden, transformControls]);

    useEffect(() => {
      const selected = completeImageObject.selectedObject;

      if (!selected || isMultiSelect || !isEventCapableSceneObject(selected)) {
        const actionKey = `${selected?.name ?? 'none'}:remove`;
        if (actionKey === prevEventsPanelActionRef.current) return;
        prevEventsPanelActionRef.current = actionKey;
        if (activeComponentsNew.includes('Events')) {
          store.dispatch(
            updateActiveComponent({
              type: 'remove',
              option: 'Events',
            })
          );
        }
        if (!isEventCapableSceneObject(selected)) {
          dispatch(setEventsPinned(false));
        }
        return;
      }

      hydrateSceneObjectEvents(selected, assets_data);

      if (isEventsPinned) return;

      const objectId = selected.name || selected._id || selected.url || null;
      const eventsCount = getEventsForSceneObject(selected, assets_data).length;
      const nextAction = eventsCount > 0 ? 'add' : 'remove';
      const actionKey = `${objectId ?? 'none'}:${nextAction}`;

      if (actionKey === prevEventsPanelActionRef.current) return;
      prevEventsPanelActionRef.current = actionKey;

      const hasEventsPanel = activeComponentsNew.includes('Events');
      if (nextAction === 'add' && !hasEventsPanel) {
        store.dispatch(
          updateActiveComponent({
            type: 'add',
            option: 'Events',
          })
        );
      } else if (nextAction === 'remove' && hasEventsPanel) {
        store.dispatch(
          updateActiveComponent({
            type: 'remove',
            option: 'Events',
          })
        );
      }
    }, [
      completeImageObject.selectedObject,
      isEventsPinned,
      assets_data,
      isMultiSelect,
      activeComponentsNew,
      dispatch,
    ]);

    // Auto-show Camera panel only when a scene camera is selected.
    useEffect(() => {
      const sceneCamera = getSelectedSceneCamera(scene, selectedObject);

      if (sceneCamera && !isMultiSelect) {
        if (!activeComponentsNew.includes('Camera')) {
          dispatch(updateActiveComponent({ option: 'Camera', type: 'add' }));
        }
      } else if (activeComponentsNew.includes('Camera')) {
        dispatch(updateActiveComponent({ option: 'Camera', type: 'remove' }));
      }
    }, [selectedObject, activeComponentsNew, dispatch, isMultiSelect, scene]);

    // Auto-show Physics when the selected object has saved physics; hide on switch otherwise.
    useEffect(() => {
      if (hiddenAutoComponents.Physics) return;

      const objectId = selectedObject?.name || null;
      const selectionChanged = prevSelectedObjectIdRef.current !== objectId;
      prevSelectedObjectIdRef.current = objectId;

      if (!objectId) {
        if (activeComponentsNew.includes('Physics')) {
          dispatch(
            updateActiveComponent({ option: 'Physics', type: 'remove' })
          );
        }
        return;
      }

      const hasPhysics = objectHasPhysicsData(selectedObject, physicsObjects);

      if (hasPhysics) {
        if (!activeComponentsNew.includes('Physics')) {
          dispatch(updateActiveComponent({ option: 'Physics', type: 'add' }));
        }
      } else if (selectionChanged && activeComponentsNew.includes('Physics')) {
        dispatch(updateActiveComponent({ option: 'Physics', type: 'remove' }));
      }
    }, [
      selectedObject,
      physicsObjects,
      activeComponentsNew,
      dispatch,
      hiddenAutoComponents.Physics,
    ]);

    // Auto-remove Material when selection is not GLB/shape/image
    useEffect(() => {
      if (
        selectedObject &&
        activeComponentsNew.includes('Material') &&
        (!shouldShowMaterialComponent(selectedObject) || blockMaterialPanel)
      ) {
        dispatch(updateActiveComponent({ option: 'Material', type: 'remove' }));
      }
    }, [selectedObject, activeComponentsNew, dispatch, blockMaterialPanel]);

    // Hide per-object property panels while multiple assets are selected.
    // Keep Material when every selected mesh shares one material (e.g. allpoints).
    useEffect(() => {
      if (!isMultiSelect) return;
      PER_OBJECT_PANELS.forEach((option) => {
        if (option === 'Material' && sharedMaterialSelection) return;
        if (activeComponentsNew.includes(option)) {
          dispatch(updateActiveComponent({ option, type: 'remove' }));
        }
      });
    }, [
      isMultiSelect,
      sharedMaterialSelection,
      PER_OBJECT_PANELS,
      activeComponentsNew,
      dispatch,
    ]);

    // Shared-material multi-select: open Material so GLB list + editor are available.
    useEffect(() => {
      if (!sharedMaterialSelection) return;
      const primary = selectedMeshesForGrouping?.[0];
      if (!primary || !shouldShowMaterialComponent(primary)) return;
      if (!activeComponentsNew.includes('Material')) {
        dispatch(updateActiveComponent({ option: 'Material', type: 'add' }));
      }
    }, [
      sharedMaterialSelection,
      selectedMeshesForGrouping,
      activeComponentsNew,
      dispatch,
    ]);

    // Auto-show HotSpot component when a hotspot is selected
    useEffect(() => {
      if (isMultiSelect) {
        if (activeComponentsNew.includes('HotSpot')) {
          dispatch(
            updateActiveComponent({ option: 'HotSpot', type: 'remove' })
          );
        }
        return;
      }

      if (
        selectedObject &&
        (selectedObject.deleteType === 'hotspot' ||
          selectedObject.userData?.isHotspot)
      ) {
        if (!activeComponentsNew.includes('HotSpot')) {
          dispatch(updateActiveComponent({ option: 'HotSpot', type: 'add' }));
        }
      } else {
        if (activeComponentsNew.includes('HotSpot')) {
          dispatch(
            updateActiveComponent({ option: 'HotSpot', type: 'remove' })
          );
        }
      }
    }, [selectedObject, activeComponentsNew, dispatch, isMultiSelect]);

    // Auto-show Video only for objects that have video attached (or are video assets).
    // Keep a just-added Video panel until the user switches selection.
    useEffect(() => {
      if (hiddenAutoComponents.Video || isMultiSelect) {
        if (activeComponentsNew.includes('Video')) {
          dispatch(updateActiveComponent({ option: 'Video', type: 'remove' }));
        }
        return;
      }

      const objectId = selectedObject?.name || selectedObject?.uuid || null;
      const selectionChanged = prevVideoSelectionIdRef.current !== objectId;
      prevVideoSelectionIdRef.current = objectId;

      if (!objectId) {
        if (activeComponentsNew.includes('Video')) {
          dispatch(updateActiveComponent({ option: 'Video', type: 'remove' }));
        }
        return;
      }

      if (objectHasVideoComponent(selectedObject)) {
        if (!activeComponentsNew.includes('Video')) {
          dispatch(updateActiveComponent({ option: 'Video', type: 'add' }));
        }
      } else if (selectionChanged && activeComponentsNew.includes('Video')) {
        dispatch(updateActiveComponent({ option: 'Video', type: 'remove' }));
      }
    }, [
      selectedObject,
      activeComponentsNew,
      dispatch,
      hiddenAutoComponents.Video,
      isMultiSelect,
    ]);

    // Reset hidden auto-components when selected object changes
    useEffect(() => {
      setHiddenAutoComponents({});
    }, [selectedObject?.name]);

    const [polyCountd, setPolyCountd] = useState(0);

    const assetTypes = ['image', 'video', 'audio', 'mesh', 'text'];

    const userName = useSelector((state) => state.user.user_name);

    const particles = useSelector((state) => state.particles.particles);

    const totalAssets =
      assetTypes.reduce(
        (sum, type) => sum + (assets_data[type]?.length || 0),
        0
      ) + (particles?.length || 0);

    useEffect(() => {
      const count = getPolyCount(scene);
      setPolyCountd(count);
    }, [totalAssets, scene, particles?.length]);

    const [sections, setSections] = useState({
      transform: true,
      properties: true,
      componentMenu: false,
    });
    const [isCollapsed, setIsCollapsed] = useState({ Environment: true });
    const [isComponentDropdownOpen, setIsComponentDropdownOpen] =
      useState(false);
    const [openDeleteDropdown, setOpenDeleteDropdown] = useState(null);

    const componentDropdownRef = useRef(null);
    const deleteDropdownRef = useRef(null);

    const allOptionAvailable = useMemo(() => {
      return allComponentOptions.filter((option) => {
        // Environment is auto-shown when nothing is selected — not addable from menu
        if (option === 'Environment') return false;
        if (option === 'Light') return true;
        if (option === 'Material' || option === 'Particle') return false;
        if (option === 'Audio' && selectedObject?.deleteType === 'audio')
          return false;
        if (option === 'Video' && objectHasVideoComponent(selectedObject))
          return false;

        if (
          option === 'Physics' &&
          objectHasPhysicsData(selectedObject, physicsObjects) &&
          !hiddenAutoComponents.Physics
        ) {
          return false;
        }

        if (
          option === 'Material' &&
          selectedObject &&
          shouldShowMaterialComponent(selectedObject)
        ) {
          return false;
        }

        // Don't show Particle in dropdown if current object is a particle (auto-shown)
        if (
          option === 'Particle' &&
          selectedObject?.deleteType === 'particle'
        ) {
          return false;
        }

        if (option === 'Events') {
          if (
            !selectedObject ||
            !isEventCapableSceneObject(selectedObject) ||
            isMultiSelect
          ) {
            return false;
          }
          return !activeComponentsNew.includes('Events');
        }

        return !activeComponentsNew.includes(option);
      });
    }, [
      selectedObject,
      activeComponentsNew,
      physicsObjects,
      isMultiSelect,
      hiddenAutoComponents.Physics,
    ]);

    const toggleSection = (section) => {
      setSections((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    };

    const cleanallanimation = async () => {
      dispatch(cleanAllAnimationObjects());

      // Reset animation system state
      window.dispatchEvent(new CustomEvent('resetAnimationState'));
    };

    const handlePublish = async () => {
      setIsPublishing(true);
      try {
        await Publish(
          (published) => {
            setPublished(published);
          },
          true,
          setName,
          completeObject,
          animationObjects,
          cleanallanimation,
          setCategory,
          setRedirectionCode
        );
      } catch (error) {
        console.error('Publish error:', error);
        setPublished(false);
      } finally {
        setIsPublishing(false);
      }
    };

    const handleBackToMenu = () => {
      setActiveDropdown(null);
      setShowMenu(true);
    };

    const handleDeleteComponent = (option) => {
      store.dispatch(updateActiveComponent({ option, type: 'remove' }));

      // For auto-shown components, hide UI only — do not remove scene / Redux data
      const autoComponents = [
        'Material',
        'Audio',
        'Text',
        'Video',
        'Environment',
        'Physics',
      ];
      if (autoComponents.includes(option)) {
        setHiddenAutoComponents((prev) => ({
          ...prev,
          [option]: true,
        }));
        if (option === 'Environment') {
          setIsEnvironmentVisible(false);
          setEnvironmentJustAdded(false);
        }
        setOpenDeleteDropdown(null);
        return;
      }

      if (option === 'Events') {
        dispatch(setEventsPinned(false));
        if (selectedObject) {
          selectedObject.events = [];
        }
      }
      if (option == 'Light') {
        DeleteMesh?.();
      }
      store.dispatch(
        lightDataUpdate({
          ...light_data,
          ...LightInitialData,
        })
      );
      setOpenDeleteDropdown(null);
    };

    const onClose = () => {
      setIsComponentDropdownOpen(false);
      setOpenDeleteDropdown(null);
    };

    const handleComponentSelect = (option) => {
      store.dispatch(updateActiveComponent({ option, type: 'add' }));
      if (option === 'Events') dispatch(setEventsPinned(true));
      if (option === 'Physics') {
        setHiddenAutoComponents((prev) => ({ ...prev, Physics: false }));
      }
      setIsComponentDropdownOpen(false);
    };

    const handlePopupToggle = (popupname) => {
      if (popupname === 'Add Component') {
        setIsComponentDropdownOpen((prev) => {
          return !prev;
        });
      } else if (popupname === 'Animation') {
        // Open the bottom animation system instead of adding an empty right-panel component
        try {
          store.dispatch(
            updateActiveComponent({ option: 'Animation', type: 'remove' })
          );
        } catch (e) {
          // no-op
        }
        globalThis.dispatchEvent(new CustomEvent('openAnimationSystem'));
      } else {
        setIsPopupOpen(isPopupOpen === popupname ? null : popupname);
        onClose();
      }
    };
    const modelRef = useRef(null);

    // Updated useClickOutside to remove Environment collapse
    useClickOutside(componentDropdownRef, () => {
      setIsComponentDropdownOpen(false);
    });

    useClickOutside(modelRef, () => {
      setIsPopupOpen(null);
      setOpenDeleteDropdown(null);
    });

    const handleMouseEnter = (option) => {
      setHoveredOption(option);
    };

    const handleMouseLeave = () => {
      setHoveredOption(null);
    };

    const selectedSceneCamera = useMemo(
      () => getSelectedSceneCamera(scene, selectedObject),
      [scene, selectedObject]
    );

    // Stable object for CameraComponent — avoid new reference every render to prevent update loop
    const cameraComponentObject = useMemo(
      () => ({
        perspectiveCamera: completeImageObject.perspectiveCamera,
        orthographicCamera: completeImageObject.orthographicCamera,
        currentCamera: selectedSceneCamera || completeImageObject.currentCamera,
        update: completeImageObject.update,
        showCameraHelper: completeImageObject.showCameraHelper,
      }),
      [
        selectedSceneCamera,
        completeImageObject.perspectiveCamera,
        completeImageObject.orthographicCamera,
        completeImageObject.currentCamera,
        completeImageObject.update,
        completeImageObject.showCameraHelper,
      ]
    );

    const renderComponent = (option) => {
      switch (option) {
        case 'Events':
          if (!isEventCapableSceneObject(selectedObject)) return null;
          return (
            <EventsComponent
              key={`events-${historyUiRevision}`}
              onBack={handleBackToMenu}
              scene={scene}
              selectedObject={selectedObject}
              setSelectedObjectState={setSelectedObjectState}
              completeImageObject={completeImageObject}
              category={category}
            />
          );
        case 'Audio':
          return (
            <AudioComponent
              key={`audio-${historyUiRevision}`}
              onBack={handleBackToMenu}
              completeAudioObject={completeImageObject}
            />
          );
        case 'Material':
          if (
            blockMaterialPanel ||
            !selectedObject ||
            !shouldShowMaterialComponent(selectedObject)
          ) {
            return null;
          }
          return (
            <MaterialComponentNew
              key={`material-${historyUiRevision}`}
              objectt={completeImageObject}
              onBack={handleBackToMenu}
            />
          );
        case 'Animation':
          return (
            <>
              {/* <AnimationComponent onBack={handleBackToMenu} /> */}
              {/* <AnimationComponentNew /> */}
            </>
          );
        case 'Video':
          return (
            <VideoComponent
              key={`video-${historyUiRevision}`}
              scene={scene}
              transformControls={transformControls}
              setPolyCount={setPolyCount}
              //  setPendingUploads={setPendingUploads}
              completeVideoObject={completeImageObject}
              onBack={handleBackToMenu}
            />
          );
        case 'Physics':
          return (
            <PhysicsComponent
              key={`physics-${historyUiRevision}`}
              historyUiRevision={historyUiRevision}
              onBack={handleBackToMenu}
              selectedObject={selectedObject}
              scene={scene}
            />
          );
        case 'Environment':
          return (
            <EnvironmentComponent
              onBack={handleBackToMenu}
              onHdriSelected={onHdriSelected}
              environmentData={environmentData}
              scene={scene}
              camera={completeImageObject?.currentCamera}
              historyUiRevision={historyUiRevision}
              key={`environment-component-${historyUiRevision}`}
            />
          );
        case 'Particle':
          return (
            <ParticleComponent
              key={`particle-${historyUiRevision}`}
              onBack={handleBackToMenu}
              particle={selectedParticle}
              onChange={(updated) => {
                const particleId = select_object?.name || selectedParticleId;
                if (particleId) {
                  dispatch(
                    updateParticle({
                      id: particleId,
                      updates: updated,
                    })
                  );
                }
              }}
            />
          );
        case 'Camera':
          if (!selectedSceneCamera) return null;
          return (
            <CameraComponent
              key={`camera-${selectedSceneCamera.uuid ?? selectedSceneCamera.name}-${historyUiRevision}`}
              object={cameraComponentObject}
              renderer={renderer}
              scene={scene}
              controlsOrbit={completeImageObject.controlsOrbit}
              cameraProperties={camera_properties}
              historyUiRevision={historyUiRevision}
            />
          );
        case 'Text':
          return (
            <TextComponent
              key={`text-${historyUiRevision}`}
              historyUiRevision={historyUiRevision}
              object={{
                scene: scene,
                selectedObject: selectedObject,
              }}
            />
          );
        case 'HotSpot':
          return (
            <HotSpotComponent
              key={`hotspot-${historyUiRevision}`}
              historyUiRevision={historyUiRevision}
              onBack={handleBackToMenu}
              selectedObject={selectedObject}
              scene={scene}
              camera={camera}
              renderer={renderer}
            />
          );
        default:
          return null;
      }
    };

    const [rotationX, setRotationX] = useState(
      selectedObject?.rotation?.x
        ? ((selectedObject.rotation.x * 180) / Math.PI).toFixed(2)
        : 0
    );
    const [rotationY, setRotationY] = useState(
      selectedObject?.rotation?.y
        ? ((selectedObject.rotation.y * 180) / Math.PI).toFixed(2)
        : 0
    );
    const [rotationZ, setRotationZ] = useState(
      selectedObject?.rotation?.z
        ? ((selectedObject.rotation.z * 180) / Math.PI).toFixed(2)
        : 0
    );
    const [positionX, setPositionX] = useState(
      selectedObject?.position?.x || 0
    );
    const [positionY, setPositionY] = useState(
      selectedObject?.position?.y || 0
    );
    const [positionZ, setPositionZ] = useState(
      selectedObject?.position?.z || 0
    );
    const [scaleX, setScaleX] = useState(1);
    const [scaleY, setScaleY] = useState(1);
    const [scaleZ, setScaleZ] = useState(1);

    // Sync transform panel from the live object (selection change + undo/redo).
    const syncTransformFieldsFromObject = useCallback((obj) => {
      if (!obj?.position) return;

      setPositionX(formatValue(obj.position.x, 2));
      setPositionY(formatValue(obj.position.y, 2));
      setPositionZ(formatValue(obj.position.z, 2));
      setRotationX(
        formatValue(obj.rotation?.x ? (obj.rotation.x * 180) / Math.PI : 0, 2)
      );
      setRotationY(
        formatValue(obj.rotation?.y ? (obj.rotation.y * 180) / Math.PI : 0, 2)
      );
      setRotationZ(
        formatValue(obj.rotation?.z ? (obj.rotation.z * 180) / Math.PI : 0, 2)
      );
      const panelScale = sceneScaleToPanelScale(obj, obj.scale);
      setScaleX(formatValue(panelScale.x, 4));
      setScaleY(formatValue(panelScale.y, 4));
      setScaleZ(formatValue(panelScale.z, 4));
    }, []);

    useEffect(() => {
      syncTransformFieldsFromObject(selectedObject);
    }, [
      selectedObject?.uuid,
      selectedObject?.name,
      historyUiRevision,
      syncTransformFieldsFromObject,
      selectedObject,
    ]);

    useEffect(() => {
      let animationFrameId;

      const updateTransform = () => {
        if (selectedObject) {
          const isDraggingTransform = !!transformControls?.dragging;
          const activeMode = transformControls?.mode;
          const pivotEditing = hasActivePivotSession();
          const pivotMarker = pivotEditing ? getPivotSessionMarker() : null;
          const positionSource = pivotMarker || selectedObject;

          const shouldSyncPosition =
            !isEditing.position &&
            (!isDraggingTransform ||
              activeMode === 'translate' ||
              pivotEditing);
          const shouldSyncRotation =
            !pivotEditing &&
            !isEditing.rotation &&
            (!isDraggingTransform || activeMode === 'rotate');
          const shouldSyncScale =
            !pivotEditing &&
            !isEditing.scale &&
            (!isDraggingTransform || activeMode === 'scale');

          if (shouldSyncPosition && positionSource?.position) {
            const newPosX = formatValue(positionSource.position?.x || 0, 2);
            const newPosY = formatValue(positionSource.position?.y || 0, 2);
            const newPosZ = formatValue(positionSource.position?.z || 0, 2);
            setPositionX((prev) => (prev !== newPosX ? newPosX : prev));
            setPositionY((prev) => (prev !== newPosY ? newPosY : prev));
            setPositionZ((prev) => (prev !== newPosZ ? newPosZ : prev));
          }

          if (shouldSyncRotation) {
            const newRotX = formatValue(
              selectedObject.rotation?.x
                ? (selectedObject.rotation.x * 180) / Math.PI
                : 0,
              2
            );
            const newRotY = formatValue(
              selectedObject.rotation?.y
                ? (selectedObject.rotation.y * 180) / Math.PI
                : 0,
              2
            );
            const newRotZ = formatValue(
              selectedObject.rotation?.z
                ? (selectedObject.rotation.z * 180) / Math.PI
                : 0,
              2
            );
            setRotationX((prev) => (prev !== newRotX ? newRotX : prev));
            setRotationY((prev) => (prev !== newRotY ? newRotY : prev));
            setRotationZ((prev) => (prev !== newRotZ ? newRotZ : prev));
          }

          if (shouldSyncScale) {
            const panelScale = sceneScaleToPanelScale(
              selectedObject,
              selectedObject.scale
            );
            const newScaleX = formatValue(panelScale.x, 4);
            const newScaleY = formatValue(panelScale.y, 4);
            const newScaleZ = formatValue(panelScale.z, 4);
            setScaleX((prev) => (prev !== newScaleX ? newScaleX : prev));
            setScaleY((prev) => (prev !== newScaleY ? newScaleY : prev));
            setScaleZ((prev) => (prev !== newScaleZ ? newScaleZ : prev));
          }
        }

        animationFrameId = requestAnimationFrame(updateTransform);
      };

      if (selectedObject) {
        updateTransform();
      }

      return () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      };
    }, [
      completeImageObject.selectedObject,
      isEditing,
      selectedObject,
      transformControls,
    ]);

    const debouncedReduxUpdate = useMemo(
      () =>
        debounce((updates) => {
          if (
            selectedObject?.deleteType === 'particle' &&
            selectedObject.name
          ) {
            store.dispatch({
              type: 'particles/updateParticle',
              payload: {
                id: selectedObject.name,
                updates,
              },
            });
          }
        }, 150),
      [selectedObject]
    );

    // Reset transform values when selectedObject is cleared (e.g., after deletion)
    useEffect(() => {
      if (!selectedObject) {
        setPositionX(0);
        setPositionY(0);
        setPositionZ(0);
        setRotationX(0);
        setRotationY(0);
        setRotationZ(0);
        setScaleX(1);
        setScaleY(1);
        setScaleZ(1);
      }
    }, [selectedObject]);

    useEffect(() => {
      const handleMouseMove = (e) => {
        if (isDragging) {
          const deltaX = e.clientX - dragStartX;
          const sensitivity = 0.01;
          const currentAxis = e.target.textContent;
          const currentScale =
            currentAxis === 'X'
              ? scaleX
              : currentAxis === 'Y'
                ? scaleY
                : scaleZ;
          const newScale = formatValue(
            Math.max(
              0.01,
              parseFloat((currentScale || 1) + deltaX * sensitivity)
            ),
            4
          );

          if (isLocked) {
            if (currentAxis === 'X') {
              setScaleX(newScale);
              setScaleY((scaleY || 0) + newScale);
              setScaleZ((scaleZ || 0) + newScale);
            } else if (currentAxis === 'Y') {
              setScaleY(newScale);
              setScaleX((scaleX || 0) + newScale);
              setScaleZ((scaleZ || 0) + newScale);
            } else if (currentAxis === 'Z') {
              setScaleZ(newScale);
              setScaleX((scaleX || 0) + newScale);
              setScaleY((scaleY || 0) + newScale);
            }
          } else {
            if (currentAxis === 'X') setScaleX(newScale);
            else if (currentAxis === 'Y') setScaleY(newScale);
            else if (currentAxis === 'Z') setScaleZ(newScale);
          }
          setDragStartX(e.clientX);
        }
      };

      const handleMouseUp = () => {
        setIsDragging(false);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }, [isDragging, dragStartX, scaleX, scaleY, scaleZ, isLocked]);

    const handleCollapseToggle = (option) => {
      setIsCollapsed((prev) => ({
        ...prev,
        [option]: !prev[option],
      }));
      setOpenDeleteDropdown(null);
    };

    const handleScaleChange = (e, axis) => {
      if (hasActivePivotSession()) return;
      if (!selectedObject) return;
      const audioType = selectedObject.deleteType === 'audio';
      const isText = selectedObject.deleteType === 'text';
      const value = e.target.value;
      const { displayValue, numericValue } =
        normalizeEditableNumericValue(value);

      if (value === '') {
        if (axis === 'x') setScaleX('');
        if (axis === 'y') setScaleY('');
        if (axis === 'z') setScaleZ('');
        return;
      }

      if (displayValue !== value) {
        if (axis === 'x') setScaleX(displayValue);
        if (axis === 'y') setScaleY(displayValue);
        if (axis === 'z') setScaleZ(displayValue);
      }

      if (numericValue === null) {
        return;
      }

      const parsed = numericValue;
      const newValue = audioType ? parsed || 0.1 : parsed;

      const applyScaleValues = (x, y, z, activeAxis, activeDisplayValue) => {
        if (isText) {
          applyPanelScaleToTextObject(selectedObject, x, y, z);
        } else {
          selectedObject.scale.set(x, y, z);
        }

        const nextX =
          activeAxis === 'x' ? activeDisplayValue : formatValue(x, 4);
        const nextY =
          activeAxis === 'y' ? activeDisplayValue : formatValue(y, 4);
        const nextZ =
          activeAxis === 'z' ? activeDisplayValue : formatValue(z, 4);

        setScaleX(nextX);
        setScaleY(nextY);
        setScaleZ(nextZ);
      };

      if (isLocked) {
        let ratios = selectedObject.userData.scaleRatios;
        if (!ratios) {
          applyScaleValues(newValue, newValue, newValue, axis, displayValue);

          if (selectedObject.deleteType === 'particle' && selectedObject.name) {
            debouncedReduxUpdate({
              scale: {
                x: selectedObject.scale.x,
                y: selectedObject.scale.y,
                z: selectedObject.scale.z,
              },
            });
          } else if (
            ['hotspot', 'mesh', 'image', 'video', 'audio', 'text'].includes(
              selectedObject.deleteType
            )
          ) {
            debouncedGridTransformUpdate(selectedObject);
          }
          return;
        }
        let newX = 1,
          newY = 1,
          newZ = 1;
        if (axis === 'x') {
          newX = newValue;
          newY = ratios.x?.y ? ratios.x.y * newValue : newValue;
          newZ = ratios.x?.z ? ratios.x.z * newValue : newValue;
        } else if (axis === 'y') {
          newY = newValue;
          newX = ratios.y?.x ? ratios.y.x * newValue : newValue;
          newZ = ratios.y?.z ? ratios.y.z * newValue : newValue;
        } else if (axis === 'z') {
          newZ = newValue;
          newX = ratios.z?.x ? ratios.z.x * newValue : newValue;
          newY = ratios.z?.y ? ratios.z.y * newValue : newValue;
        }
        applyScaleValues(newX, newY, newZ, axis, displayValue);

        if (selectedObject.deleteType === 'particle' && selectedObject.name) {
          debouncedReduxUpdate({
            scale: {
              x: selectedObject.scale.x,
              y: selectedObject.scale.y,
              z: selectedObject.scale.z,
            },
          });
        } else if (
          ['hotspot', 'mesh', 'image', 'video', 'audio', 'text'].includes(
            selectedObject.deleteType
          )
        ) {
          debouncedGridTransformUpdate(selectedObject);
        }
      } else {
        const panelScale = isText
          ? sceneScaleToPanelScale(selectedObject, selectedObject.scale)
          : {
              x: selectedObject.scale.x,
              y: selectedObject.scale.y,
              z: selectedObject.scale.z,
            };

        if (axis === 'x') panelScale.x = newValue;
        else if (axis === 'y') panelScale.y = newValue;
        else if (axis === 'z') panelScale.z = newValue;

        applyScaleValues(
          panelScale.x,
          panelScale.y,
          panelScale.z,
          axis,
          displayValue
        );

        const ratioSource = isText
          ? panelScale
          : {
              x: selectedObject.scale.x,
              y: selectedObject.scale.y,
              z: selectedObject.scale.z,
            };
        selectedObject.userData.scaleRatios = {
          x: {
            y: ratioSource.y / ratioSource.x,
            z: ratioSource.z / ratioSource.x,
          },
          y: {
            x: ratioSource.x / ratioSource.y,
            z: ratioSource.z / ratioSource.y,
          },
          z: {
            x: ratioSource.x / ratioSource.z,
            y: ratioSource.y / ratioSource.z,
          },
        };

        if (selectedObject.deleteType === 'particle' && selectedObject.name) {
          debouncedReduxUpdate({
            scale: {
              x: selectedObject.scale.x,
              y: selectedObject.scale.y,
              z: selectedObject.scale.z,
            },
          });
        } else if (
          ['hotspot', 'mesh', 'image', 'video', 'audio', 'text'].includes(
            selectedObject.deleteType
          )
        ) {
          debouncedGridTransformUpdate(selectedObject);
        }
      }
    };

    const handleRotationChange = (e, axis) => {
      if (hasActivePivotSession()) return;
      const value = e.target.value;
      if (/^-?\d*\.?\d{0,2}$/.test(value)) {
        const parsed = parseFloat(value);
        const newValue = value === '' ? '' : isNaN(parsed) ? 0 : parsed;

        if (axis === 'x') setRotationX(newValue);
        if (axis === 'y') setRotationY(newValue);
        if (axis === 'z') setRotationZ(newValue);

        if (selectedObject && newValue !== '') {
          const rad = (newValue * Math.PI) / 180;

          const currentRot = {
            x: selectedObject.rotation.x,
            y: selectedObject.rotation.y,
            z: selectedObject.rotation.z,
          };

          selectedObject.rotation.set(
            axis === 'x' ? rad : currentRot.x,
            axis === 'y' ? rad : currentRot.y,
            axis === 'z' ? rad : currentRot.z
          );
          // If the selected object is a light helper, sync its transform to the actual light
          try {
            if (
              selectedObject.name &&
              selectedObject.name.endsWith('-helper')
            ) {
              const lightId = selectedObject.name.replace('-helper', '');
              const actualLight = scene && scene.getObjectByName(lightId);
              if (actualLight) {
                actualLight.rotation.copy(selectedObject.rotation);
                if (typeof selectedObject.update === 'function')
                  selectedObject.update();
                // Update light data in Redux so UI and persistence stay in sync
                store.dispatch(
                  lightDataUpdate({
                    id: lightId,
                    rotation: {
                      x: actualLight.rotation.x,
                      y: actualLight.rotation.y,
                      z: actualLight.rotation.z,
                    },
                  })
                );
              }
            }
          } catch (err) {
            // no-op, keep original behavior
          }

          if (selectedObject.deleteType === 'particle' && selectedObject.name) {
            debouncedReduxUpdate({
              rotation: {
                x: selectedObject.rotation.x,
                y: selectedObject.rotation.y,
                z: selectedObject.rotation.z,
              },
            });
          } else if (
            ['hotspot', 'mesh', 'image', 'video', 'audio', 'text'].includes(
              selectedObject.deleteType
            )
          ) {
            debouncedGridTransformUpdate(selectedObject);
          }
        }
      }
    };

    const handlePositionChange = (e, axis) => {
      let value = e.target.value;

      if (value === '') {
        if (axis === 'x') setPositionX('');
        if (axis === 'y') setPositionY('');
        if (axis === 'z') setPositionZ('');
        return;
      }

      if (/^-?\d*\.?\d{0,4}$/.test(value)) {
        const parsed = parseFloat(value);
        const newValue = value === '' ? '' : isNaN(parsed) ? 0 : parsed;

        if (axis === 'x') setPositionX(newValue);
        if (axis === 'y') setPositionY(newValue);
        if (axis === 'z') setPositionZ(newValue);

        // While editing pivot, drive the marker — not the asset transform.
        if (hasActivePivotSession() && newValue !== '') {
          const pivotMarker = getPivotSessionMarker();
          if (pivotMarker?.position) {
            const currentPos = pivotMarker.position;
            pivotMarker.position.set(
              axis === 'x' ? newValue : currentPos.x,
              axis === 'y' ? newValue : currentPos.y,
              axis === 'z' ? newValue : currentPos.z
            );
          }
          return;
        }

        if (selectedObject && newValue !== '') {
          const currentPos = selectedObject.position;
          selectedObject.position.set(
            axis === 'x' ? newValue : currentPos.x,
            axis === 'y' ? newValue : currentPos.y,
            axis === 'z' ? newValue : currentPos.z
          );
          // If the selected object is a light helper, sync its transform to the actual light
          try {
            if (
              selectedObject.name &&
              selectedObject.name.endsWith('-helper')
            ) {
              const lightId = selectedObject.name.replace('-helper', '');
              const actualLight = scene && scene.getObjectByName(lightId);
              if (actualLight) {
                actualLight.position.copy(selectedObject.position);
                if (typeof selectedObject.update === 'function')
                  selectedObject.update();
                // Update light data in Redux so UI and persistence stay in sync
                store.dispatch(
                  lightDataUpdate({
                    id: lightId,
                    position: {
                      x: actualLight.position.x,
                      y: actualLight.position.y,
                      z: actualLight.position.z,
                    },
                  })
                );
              }
            }
          } catch (err) {
            // no-op
          }
          // If selected object is a light helper, propagate scale change (if applicable)
          try {
            if (
              selectedObject?.name &&
              selectedObject.name.endsWith('-helper')
            ) {
              const lightId = selectedObject.name.replace('-helper', '');
              const actualLight = scene && scene.getObjectByName(lightId);
              if (actualLight) {
                actualLight.scale.copy(selectedObject.scale);
                if (typeof selectedObject.update === 'function')
                  selectedObject.update();
                store.dispatch(
                  lightDataUpdate({
                    id: lightId,
                    scale: {
                      x: actualLight.scale.x,
                      y: actualLight.scale.y,
                      z: actualLight.scale.z,
                    },
                  })
                );
              }
            }
          } catch (err) {
            // ignore
          }

          if (selectedObject.deleteType === 'particle' && selectedObject.name) {
            debouncedReduxUpdate({
              position: {
                x: selectedObject.position.x,
                y: selectedObject.position.y,
                z: selectedObject.position.z,
              },
            });
          } else if (
            ['hotspot', 'mesh', 'image', 'video', 'audio', 'text'].includes(
              selectedObject.deleteType
            )
          ) {
            debouncedGridTransformUpdate(selectedObject);
          }
        }
      }
    };

    const handlePositionBlur = (axis) => {
      setIsEditing((prev) => ({ ...prev, position: false }));

      // Ensure the value is properly formatted
      const value =
        axis === 'x' ? positionX : axis === 'y' ? positionY : positionZ;
      const formatted = formatValue(value, 4);

      if (axis === 'x') setPositionX(formatted);
      if (axis === 'y') setPositionY(formatted);
      if (axis === 'z') setPositionZ(formatted);
    };

    const handleRotationBlur = (axis) => {
      setIsEditing((prev) => ({ ...prev, rotation: false }));

      const value =
        axis === 'x' ? rotationX : axis === 'y' ? rotationY : rotationZ;
      const formatted = formatValue(value, 2);

      if (axis === 'x') setRotationX(formatted);
      if (axis === 'y') setRotationY(formatted);
      if (axis === 'z') setRotationZ(formatted);
    };

    const handleScaleBlur = (axis) => {
      setIsEditing((prev) => ({ ...prev, scale: false }));

      const value = axis === 'x' ? scaleX : axis === 'y' ? scaleY : scaleZ;
      const formatted = formatValue(value, 4);

      if (axis === 'x') setScaleX(formatted);
      if (axis === 'y') setScaleY(formatted);
      if (axis === 'z') setScaleZ(formatted);
    };

    const handleDragValue = useCallback(
      (initialValue = 1, setLocalValue, onChange, options = {}) => {
        const {
          min = -Infinity,
          max = Infinity,
          sensitivity = 0.01,
          precision = 4,
          deadZone = 0.5,
        } = options;
        let startX = null;
        let startValue = null;
        let rafId = null;

        const onMouseMove = (e) => {
          if (startX === null) return;
          const deltaPx = e.clientX - startX;

          if (Math.abs(deltaPx) < deadZone) return;

          if (rafId) cancelAnimationFrame(rafId);

          rafId = requestAnimationFrame(() => {
            let newValue = Number(startValue) + deltaPx * sensitivity;
            newValue = Math.max(min, Math.min(max, newValue));

            const factor = Math.pow(10, precision);
            newValue = Math.round(newValue * factor) / factor;

            // Prevent empty value: always set a valid number
            if (isNaN(newValue)) newValue = 0;

            if (newValue !== startValue) {
              setLocalValue(newValue);
              if (onChange) onChange({ target: { value: newValue } });
            }
          });
        };

        const onMouseUp = () => {
          if (rafId) cancelAnimationFrame(rafId);
          window.removeEventListener('mousemove', onMouseMove);
          window.removeEventListener('mouseup', onMouseUp);
          startX = null;
          startValue = null;
        };

        return (e) => {
          e.preventDefault();
          startX = e.clientX;
          // Always use a valid number for startValue
          startValue = Number(initialValue) || 0;
          window.addEventListener('mousemove', onMouseMove);
          window.addEventListener('mouseup', onMouseUp);
        };
      },
      []
    );

    useEffect(() => {
      return () => {};
    }, []);

    const PositionMouseDown = (e, axis) => {
      const setLocalValue =
        axis === 'X'
          ? setPositionX
          : axis === 'Y'
            ? setPositionY
            : setPositionZ;
      const currentValue =
        axis === 'X' ? positionX : axis === 'Y' ? positionY : positionZ;

      handleDragValue(
        currentValue,
        setLocalValue,
        (e) => handlePositionChange(e, axis.toLowerCase()),
        { sensitivity: 0.01, precision: 2, deadZone: 1 }
      )(e);
    };

    const RotationMouseDown = (e, axis) => {
      const setLocalValue =
        axis === 'X'
          ? setRotationX
          : axis === 'Y'
            ? setRotationY
            : setRotationZ;
      const currentValue =
        axis === 'X' ? rotationX : axis === 'Y' ? rotationY : rotationZ;

      handleDragValue(
        currentValue,
        setLocalValue,
        (e) => handleRotationChange(e, axis.toLowerCase()),
        { sensitivity: 0.5, precision: 1, deadZone: 1 }
      )(e);
    };

    const ScaleMouseDown = (e, axis) => {
      const setLocalValue =
        axis === 'X' ? setScaleX : axis === 'Y' ? setScaleY : setScaleZ;
      const currentValue =
        axis === 'X' ? scaleX : axis === 'Y' ? scaleY : scaleZ;

      handleDragValue(
        currentValue,
        setLocalValue,
        (e) => handleScaleChange(e, axis.toLowerCase()),
        { min: 0.01, sensitivity: 0.01, precision: 2, deadZone: 1 }
      )(e);
    };

    const isPivotEditing = hasActivePivotSession();

    const TransformControllerData = [
      {
        title: 'Position',
        onChange: handlePositionChange,
        onFocus: () => setIsEditing((prev) => ({ ...prev, position: true })),
        onBlur: handlePositionBlur,
        step: '0.01',
        data: [
          {
            label: 'X',
            value: positionX,
            onMouseDown: (e) => PositionMouseDown(e, 'X'),
          },
          {
            label: 'Y',
            value: positionY,
            onMouseDown: (e) => PositionMouseDown(e, 'Y'),
          },
          {
            label: 'Z',
            value: positionZ,
            onMouseDown: (e) => PositionMouseDown(e, 'Z'),
          },
        ],
      },
      {
        title: 'Rotation',
        onChange: handleRotationChange,
        onFocus: () => setIsEditing((prev) => ({ ...prev, rotation: true })),
        onBlur: handleRotationBlur,
        step: '1',
        data: [
          {
            label: 'X',
            value: rotationX,
            readOnly: isPivotEditing,
            onMouseDown: isPivotEditing
              ? undefined
              : (e) => RotationMouseDown(e, 'X'),
          },
          {
            label: 'Y',
            value: rotationY,
            readOnly: isPivotEditing,
            onMouseDown: isPivotEditing
              ? undefined
              : (e) => RotationMouseDown(e, 'Y'),
          },
          {
            label: 'Z',
            value: rotationZ,
            readOnly: isPivotEditing,
            onMouseDown: isPivotEditing
              ? undefined
              : (e) => RotationMouseDown(e, 'Z'),
          },
        ],
      },
      {
        title: 'Scale',
        onChange: handleScaleChange,
        onFocus: () => setIsEditing((prev) => ({ ...prev, scale: true })),
        onBlur: handleScaleBlur,
        step: '0.01',
        data: [
          {
            label: 'X',
            value: scaleX,
            readOnly: isPivotEditing,
            onMouseDown: isPivotEditing
              ? undefined
              : (e) => ScaleMouseDown(e, 'X'),
          },
          {
            label: 'Y',
            value: scaleY,
            readOnly: isPivotEditing,
            onMouseDown: isPivotEditing
              ? undefined
              : (e) => ScaleMouseDown(e, 'Y'),
          },
          {
            label: 'Z',
            value: scaleZ,
            readOnly:
              isPivotEditing ||
              (selectedObject?.deleteType === 'text' &&
                selectedObject?.userData?.text_material?.textType === '2D'),
            onMouseDown: isPivotEditing
              ? undefined
              : (e) => ScaleMouseDown(e, 'Z'),
          },
        ],
      },
    ];

    const ShowRightCompoenents = useCallback(() => {
      if (isMultiSelect || !selectedObject) return '';
      if (
        shouldShowMaterialComponent(selectedObject) &&
        activeComponentsNew.includes('Material')
      ) {
        return '';
      }
      let component =
        selectedObject.deleteType === 'audio' &&
        !activeComponentsNew.includes('Audio')
          ? 'Audio'
          : objectHasVideoComponent(selectedObject) &&
              !activeComponentsNew.includes('Video')
            ? 'Video'
            : shouldShowMaterialComponent(selectedObject)
              ? 'Material'
              : selectedObject.deleteType === 'text'
                ? 'Text'
                : selectedObject.deleteType === 'Environment'
                  ? 'Environment'
                  : '';

      // Check if component is hidden
      if (hiddenAutoComponents[component]) {
        return '';
      }

      return component;
    }, [
      selectedObject,
      hiddenAutoComponents,
      isMultiSelect,
      activeComponentsNew,
      scene,
      select_object,
    ]);

    const isActiveComponentVisible = useCallback(
      (option) => {
        if (
          isMultiSelect &&
          PER_OBJECT_PANELS.includes(option) &&
          !(option === 'Material' && sharedMaterialSelection)
        ) {
          return false;
        }
        if (option === 'Light') return false;
        if (option === 'Environment' && !isEnvironmentVisible) return false;
        if (option === 'Events' && !isEventCapableSceneObject(selectedObject)) {
          return false;
        }
        if (option === 'Camera' && !selectedSceneCamera) return false;
        if (hiddenAutoComponents[option]) return false;
        return true;
      },
      [
        isMultiSelect,
        sharedMaterialSelection,
        PER_OBJECT_PANELS,
        isEnvironmentVisible,
        selectedObject,
        selectedSceneCamera,
        hiddenAutoComponents,
      ]
    );

    const selectedParticleId = useSelector(
      (state) => state.particles.selectedParticle
    );

    const selectedParticle = particles.find(
      (p) => p.id === select_object?.name
    );

    const selectedLightId = useSelector(
      (state) => state.global_state.selectedLightId
    );
    const selectedLight = light_data.find(
      (l) =>
        l.id === selectedLightId ||
        l.id === select_object?.name ||
        l.name === selectedLightId ||
        l.name === select_object?.name
    );

    const autoShownComponentName = ShowRightCompoenents();
    const showAutoComponentPanel = Boolean(
      selectedObject &&
        selectedObject.name &&
        selectedObject?.deleteType !== 'Light' &&
        !isMultiSelect &&
        autoShownComponentName
    );
    const showParticlePanel = Boolean(
      selectedObject &&
        ['particle'].includes(selectedObject.deleteType) &&
        !activeComponentsNew.includes('Particle')
    );
    const showLightPanel = Boolean(
      selectedLight &&
        !isMultiSelect &&
        (select_object?.type === 'DirectionalLight' ||
          select_object?.type === 'PointLight' ||
          select_object?.type === 'SpotLight' ||
          select_object?.type === 'AmbientLight' ||
          select_object?.type === 'RectAreaLight')
    );
    // Avoid double panels/dividers (e.g. Environment shown in both
    // auto panel and activeComponentsNew).
    const visibleActiveComponents = useMemo(
      () =>
        activeComponentsNew.filter(
          (option) =>
            isActiveComponentVisible(option) &&
            !(showAutoComponentPanel && option === autoShownComponentName)
        ),
      [
        activeComponentsNew,
        isActiveComponentVisible,
        showAutoComponentPanel,
        autoShownComponentName,
      ]
    );
    const hasPanelsAboveActiveComponents =
      showAutoComponentPanel || showParticlePanel || showLightPanel;

    return (
      <div
        className="control-panel overflow-hidden overflow-x-hidden custom-scrollbar"
        ref={modelRef}
        style={{
          height: isAnimationMinimized
            ? 'calc(100% - 40px)'
            : `calc(100% - ${animationHeight - 6}px)`,
          transition: isAnimationResizing ? 'none' : 'height 0.3s ease-in-out',
        }}
      >
        <div className="header">
          <div className="left-controls">
            <div className="circle-button-outer">
              <button className="circle-button">
                {userName?.charAt(0)?.toUpperCase() || 'S'}
              </button>
            </div>
            <button className="icon-button">
              <img
                className="icon-button-img"
                src={iconsPath?.group141Image}
                alt=""
              />
              <span style={{ paddingTop: '2px' }}>Export</span>
            </button>
          </div>
          <button
            className={`publish-button ${isPublishing ? 'opacity-75' : ''}`}
            onClick={handlePublish}
            disabled={isPublishing}
          >
            <div className="flex items-center justify-center gap-2">
              {isPublishing ? <PublishSpinner /> : null}
              <span style={{ paddingTop: '1px' }}>
                {isPublishing ? '' : 'Publish'}
              </span>
            </div>
          </button>
        </div>

        <div className="relative" ref={componentDropdownRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePopupToggle('Add Component');
            }}
            className="add-component"
            style={{ minHeight: '30px' }}
          >
            <span className="add-component-span">
              <img
                className="add-component-img"
                src={iconsPath?.group290Image}
                alt=""
              />
            </span>{' '}
            Add a Component
          </button>

          {isComponentDropdownOpen && (
            <ModalAdd
              closeModal={() => {
                setIsComponentDropdownOpen(false);
              }}
              isOpen={isComponentDropdownOpen}
              inset="false"
              className="w-[300px]"
            >
              <div className="min-h-auto min-w-[190px] z-10 w-full bg-[#1A1B1E] rounded-md shadow-lg mt-1">
                <ul className="py-2">
                  {allOptionAvailable.length === 0 ? (
                    <li className="text-center px-4 py-2 text-gray-400 text-xs font-inter">
                      No available components
                    </li>
                  ) : (
                    allOptionAvailable.map((option, index) => (
                      <li key={index}>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsComponentDropdownOpen(false);
                            // if(option === 'Events'){
                            //   navigate('/vr-mindmap/'+projectId);
                            // }
                            if (option === 'Light') {
                              // Generate unique id for the new light
                              const newId =
                                'light-' +
                                Math.random().toString(36).substr(2, 9);
                              const newLight = {
                                ...LightInitialData,
                                id: newId,
                                light_type: 'Directional Light', // Set default light type
                                name: 'Directional Light',
                              };

                              // Add light to Redux store
                              store.dispatch(addLight(newLight));

                              store.dispatch(setSelectedLightId(newId));

                              store.dispatch(
                                updateActiveComponent({
                                  type: 'add',
                                  option: 'Light',
                                })
                              );

                              scheduleSelectLightInScene(
                                scene,
                                newId,
                                selectObjectInScene
                              );
                            } else {
                              if (option === 'Animation') {
                                // Open the bottom animation system instead of adding an empty right-panel component
                                try {
                                  store.dispatch(
                                    updateActiveComponent({
                                      type: 'remove',
                                      option: 'Animation',
                                    })
                                  );
                                } catch (e) {
                                  // no-op
                                }
                                globalThis.dispatchEvent(
                                  new CustomEvent('openAnimationSystem')
                                );
                                return;
                              }
                              store.dispatch(
                                updateActiveComponent({ type: 'add', option })
                              );
                              if (option === 'Events')
                                dispatch(setEventsPinned(true));
                              if (option === 'Environment') {
                                store.dispatch(
                                  setEnvironmentData({
                                    ...environmentData,
                                    visibility: true,
                                  })
                                );
                                setHiddenAutoComponents((prev) => {
                                  const next = { ...prev };
                                  delete next.Environment;
                                  return next;
                                });
                                setIsEnvironmentVisible(true);
                                setEnvironmentJustAdded(true);
                                setIsCollapsed((prev) => ({
                                  ...prev,
                                  Environment: true,
                                }));
                              }
                            }
                          }}
                          onMouseEnter={() => handleMouseEnter(option)}
                          onMouseLeave={handleMouseLeave}
                          className={`px-4 py-2 hover:bg-[#2B2C2F] cursor-pointer font-inter text-xs flex justify-between items-center transition-colors duration-150`}
                        >
                          <span className="text-[10px]">{option}</span>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </ModalAdd>
          )}
        </div>
        <DividerRight />
        <div className="flex h-[calc(100vh-64px)] flex-col overflow-auto  scrollbar ">
          <div className="app-right-section">
            <div
              className="right-section-header"
              onClick={() => toggleSection('transform')}
            >
              <span className="section-header-title">Transform Controls</span>
              <div
                className="flex items-center ml-2 cursor-pointer p-1 rounded transition-colors relative group"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSelectionBoxHidden(!isSelectionBoxHidden);
                }}
                onMouseEnter={() => {
                  console.log('Selection Box Hovered');
                  setShowTooltip(true);
                }}
                onMouseLeave={() => setShowTooltip(false)}
              >
                {isSelectionBoxHidden ? (
                  <PiEyeSlashThin size={16} className="text-gray-400" />
                ) : (
                  <PiEyeThin size={16} className="text-white" />
                )}
                {showTooltip && (
                  <span className="absolute top-full mt-2 left-1/2 translate-x-3 -translate-y-10 bg-[#1e1f22] text-white text-[10px] rounded px-2 py-1 opacity-100 transition-opacity z-[9999] whitespace-nowrap border border-[#2B2B2B]">
                    Selection Box
                  </span>
                )}
              </div>
              <div
                className="flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
                style={{ marginRight: 'auto', marginLeft: '10px' }}
              ></div>
              <span>
                <img
                  src={iconsPath?.vectorImage}
                  className={`transform transition-transform duration-300 ${
                    sections.transform ? 'rotate-180' : 'rotate-0'
                  }`}
                />
              </span>
            </div>

            {sections.transform && (
              <div>
                {TransformControllerData.map((val, index) => (
                  <div className="control-row" key={index}>
                    <span className="right-text">
                      <p style={{ paddingTop: '4px', margin: '0px' }}>
                        {val?.title}
                      </p>
                      {val?.title == 'Scale' && (
                        <img
                          src={
                            isLocked
                              ? iconsPath?.vector4Image
                              : iconsPath?.Unlockvector5Image
                          }
                          alt={isLocked ? 'Locked' : 'Unlocked'}
                          style={{
                            cursor: 'pointer',
                            width: '12px',
                            height: '12px',
                          }}
                          onClick={() => setIsLocked(!isLocked)}
                        />
                      )}
                    </span>
                    <div className="axis-buttons">
                      {val?.data?.map((v, i) => (
                        <div key={i} className="axis-button-child">
                          <p
                            className="axis-para"
                            style={{
                              cursor: 'ew-resize',
                              color:
                                AXIS_LABEL_COLORS[v?.label] || '#a6a6a6',
                            }}
                            onMouseDown={(e) => {
                              if (v?.onMouseDown) {
                                v?.onMouseDown(e, v?.label);
                              }
                            }}
                          >
                            {v?.label}
                          </p>
                          <input
                            type="number"
                            id={val.title?.toLowerCase() + v?.label}
                            className="axis-button appearance-none"
                            value={
                              v?.value === undefined || v?.value === null
                                ? ''
                                : v?.value
                            }
                            readOnly={v?.readOnly}
                            onChange={(e) =>
                              val?.onChange(e, v.label?.toLowerCase())
                            }
                            onFocus={val?.onFocus}
                            onBlur={() => val?.onBlur(v.label?.toLowerCase())}
                            step={val?.step}
                            inputMode="decimal"
                            onWheel={(e) => e.target.blur()}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <DividerRight />
          </div>
          {showAutoComponentPanel && (
            <div className="w-55 text-white flex-shrink-0 text-xs inline-block align-top">
              {autoShownComponentName === 'Environment' ? (
                <>
                  <div
                    className="py-2 flex justify-between items-center cursor-pointer"
                    onClick={() => handleCollapseToggle('Environment')}
                  >
                    <span>Environment</span>
                    <img
                      src={iconsPath?.vectorImage}
                      alt=""
                      className={`transform transition-transform duration-300 ${
                        !isCollapsed.Environment ? 'rotate-180' : 'rotate-0'
                      }`}
                    />
                  </div>
                  {!isCollapsed.Environment &&
                    renderComponent('Environment')}
                </>
              ) : (
                <>
                  <div className="py-2 flex justify-between items-center cursor-pointer">
                    <span>{autoShownComponentName}</span>
                    <div
                      className="relative"
                      ref={
                        openDeleteDropdown === autoShownComponentName
                          ? deleteDropdownRef
                          : null
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 cursor-pointer"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        onClick={() =>
                          setOpenDeleteDropdown(
                            openDeleteDropdown === autoShownComponentName
                              ? null
                              : autoShownComponentName
                          )
                        }
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v.01M12 12v.01M12 18v.01"
                        />
                      </svg>
                      {openDeleteDropdown === autoShownComponentName && (
                        <div className="absolute z-10 right-0 mt-1 w-24 bg-[#1A1B1E] rounded-md shadow-lg">
                          <ul className="py-1">
                            <li>
                              <div
                                onClick={() =>
                                  handleDeleteComponent(autoShownComponentName)
                                }
                                onMouseEnter={() =>
                                  handleMouseEnter(
                                    `remove-${autoShownComponentName}`
                                  )
                                }
                                onMouseLeave={handleMouseLeave}
                                className={`px-2 py-0.5 cursor-pointer font-inter text-[10px] text-white transition-colors duration-150 ${
                                  hoveredOption ===
                                  `remove-${autoShownComponentName}`
                                    ? 'bg-[#2B2C2F]'
                                    : ''
                                }`}
                              >
                                Remove
                              </div>
                            </li>
                            <li>
                              <div
                                onClick={() =>
                                  handleCollapseToggle(autoShownComponentName)
                                }
                                onMouseEnter={() =>
                                  handleMouseEnter(
                                    `collapse-${autoShownComponentName}`
                                  )
                                }
                                onMouseLeave={handleMouseLeave}
                                className={`px-2 py-0.5 cursor-pointer font-inter text-[10px] text-white transition-colors duration-150 ${
                                  hoveredOption ===
                                  `collapse-${autoShownComponentName}`
                                    ? 'bg-[#2B2C2F]'
                                    : ''
                                }`}
                              >
                                {isCollapsed[autoShownComponentName]
                                  ? 'Expand'
                                  : 'Collapse'}
                              </div>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  {!isCollapsed[autoShownComponentName] &&
                    renderComponent(autoShownComponentName)}
                </>
              )}
            </div>
          )}

          {showParticlePanel && (
            <>
              {showAutoComponentPanel && <DividerRight />}
              <div className="py-2">
                <span className="text-[12px]">Particle Properties</span>
                <ParticleComponent
                  key={`particle-panel-${historyUiRevision}`}
                  particle={selectedParticle}
                  onChange={(updated) => {
                    const particleId =
                      select_object?.name || selectedParticleId;
                    if (particleId) {
                      dispatch(
                        updateParticle({
                          id: particleId,
                          updates: updated,
                        })
                      );
                    }
                  }}
                />
              </div>
            </>
          )}

          {showLightPanel && (
            <>
              {(showAutoComponentPanel || showParticlePanel) && (
                <DividerRight />
              )}
              <LightComponent
                key={`light-${selectedLight?.id ?? selectedLight?.name}-${historyUiRevision}`}
                light={selectedLight}
                showLightHelpers={showLightHelpers}
                historyUiRevision={historyUiRevision}
              />
            </>
          )}

          {isMultiSelect && !sharedMaterialSelection && (
            <div className="px-2 py-3 text-[10px] text-gray-400 leading-relaxed">
              Multiple objects selected. Select a single object to edit its
              material and other properties.
            </div>
          )}

          {sharedMaterialSelection && (
            <div className="px-2 py-2 text-[10px] text-gray-500 leading-relaxed">
              {selectedMeshesForGrouping.length} meshes share this material.
              Edits apply to all of them.
            </div>
          )}

          {visibleActiveComponents.map((option, index) => (
            <React.Fragment key={option}>
              {(index > 0 ||
                (index === 0 && hasPanelsAboveActiveComponents)) && (
                <DividerRight />
              )}
              <div className="w-55 text-white flex-shrink-0 text-xs inline-block align-top">
                {option === 'Environment' ? (
                  <>
                    <div
                      className="py-2 flex justify-between items-center cursor-pointer"
                      onClick={() => handleCollapseToggle('Environment')}
                    >
                      <span>{option}</span>
                      <img
                        src={iconsPath?.vectorImage}
                        alt=""
                        className={`transform transition-transform duration-300 ${
                          !isCollapsed.Environment ? 'rotate-180' : 'rotate-0'
                        }`}
                      />
                    </div>
                    {!isCollapsed.Environment &&
                      isEnvironmentVisible &&
                      renderComponent('Environment')}
                  </>
                ) : (
                  <>
                    <div className="py-2 flex justify-between items-center cursor-pointer">
                      <span>{option}</span>
                      <div className="relative">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 cursor-pointer"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          onClick={() =>
                            setOpenDeleteDropdown(
                              openDeleteDropdown === option ? null : option
                            )
                          }
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v.01M12 12v.01M12 18v.01"
                          />
                        </svg>
                        {openDeleteDropdown === option && (
                          <div className="absolute z-10 right-0 mt-1 w-24 bg-[#1A1B1E] rounded-md shadow-lg">
                            <ul className="py-1">
                              <li>
                                <div
                                  onClick={() => handleDeleteComponent(option)}
                                  onMouseEnter={() =>
                                    handleMouseEnter(`delete-${option}`)
                                  }
                                  onMouseLeave={handleMouseLeave}
                                  className={`px-2 py-0.5 cursor-pointer font-inter text-[10px] text-white transition-colors duration-150 ${
                                    hoveredOption === `delete-${option}`
                                      ? 'bg-[#2B2C2F]'
                                      : ''
                                  }`}
                                >
                                  Remove
                                </div>
                              </li>
                              <li>
                                <div
                                  onClick={() => handleCollapseToggle(option)}
                                  onMouseEnter={() =>
                                    handleMouseEnter(`collapse-${option}`)
                                  }
                                  onMouseLeave={handleMouseLeave}
                                  className={`px-2 py-0.5 cursor-pointer font-inter text-[10px] text-white transition-colors duration-150 ${
                                    hoveredOption === `collapse-${option}`
                                      ? 'bg-[#2B2C2F]'
                                      : ''
                                  }`}
                                >
                                  {isCollapsed[option] ? 'Expand' : 'Collapse'}
                                </div>
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                    {!isCollapsed[option] && renderComponent(option)}
                  </>
                )}
              </div>
            </React.Fragment>
          ))}

          {sections.componentMenu && (
            <div className="w-55 text-white flex-shrink-0 text-xs inline-block align-top">
              <DividerRight />
              {!showMenu &&
              activeDropdown &&
              !activeComponentsNew.includes(activeDropdown) ? (
                <div className="py-">
                  <div
                    className="py-2 flex justify-between items-center cursor-pointer"
                    onClick={handleBackToMenu}
                    onMouseEnter={() =>
                      setHoveredOption(`${activeDropdown}Header`)
                    }
                    onMouseLeave={handleMouseLeave}
                  >
                    <span>{activeDropdown}</span>
                    {hoveredOption === `${activeDropdown}Header` && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 rotate-180 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    )}
                  </div>
                  {renderComponent(activeDropdown)}
                </div>
              ) : (
                <div className="py-2">
                  <ul>
                    {allOptionAvailable.map((option, index) => (
                      <li key={index}>
                        <div
                          onClick={() => {
                            setSections((prev) => ({
                              ...prev,
                              componentMenu: false,
                            }));
                          }}
                          onMouseEnter={() => handleMouseEnter(option)}
                          onMouseLeave={handleMouseLeave}
                          className={`px-2 py-2 hover:bg-[#2B2C2F] rounded cursor-pointer font-inter transition-colors duration-150 flex justify-between items-center text-xs ${
                            activeDropdown === option ? 'bg-gray-800' : ''
                          }`}
                        >
                          <span>{option}</span>
                          {(hoveredOption === option ||
                            activeDropdown === option) && (
                            <img
                              className={`${
                                activeDropdown === option ? 'rotate-180' : ''
                              }`}
                              src={iconsPath?.vectorImage}
                            />
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {isPopupOpen === 'Help' && (
            <SupportMenu
              isPopupOpen={isPopupOpen}
              isAnimationMinimized={isAnimationMinimized}
              animationHeight={animationHeight}
              handlePopupToggle={handlePopupToggle}
              setIsPopupOpen={setIsPopupOpen}
            />
          )}

          {isPopupOpen === 'Shortcuts' && (
            <SimpleModal
              isOpen={isPopupOpen === 'Shortcuts'}
              onClose={() => setIsPopupOpen('')}
            >
              <RightMenuShortcutKeys
                closeModal={() => handlePopupToggle(null)}
                setIsPopupOpen={setIsPopupOpen}
                handlePopupToggle={handlePopupToggle}
              />
            </SimpleModal>
          )}

          {isPopupOpen === 'Learning' && (
            <SimpleModal
              className={'!min-w-fit flex justify-center items-start'}
              isOpen={isPopupOpen === 'Learning'}
              onClose={() => setIsPopupOpen('')}
            >
              <LearningCenter closeModal={() => handlePopupToggle(null)} />
            </SimpleModal>
          )}

          {isPopupOpen === 'Feedback' && (
            <SimpleModal
              isOpen={isPopupOpen === 'Feedback'}
              onClose={() => setIsPopupOpen('')}
            >
              <FeedbackModal closeModal={() => handlePopupToggle(null)} />
            </SimpleModal>
          )}
        </div>
        <div className="footer mt-auto">
          <button
            onClick={() => handlePopupToggle('Help')}
            className="help-button"
          >
            <img className="help-icon" src={iconsPath?.questionImage} alt="" />
          </button>
          <span className="status">
            Objects: {totalAssets} | Poly Count: {polyCountd}
          </span>
          {isBetaCategory && (
            <span className="ml-auto text-[#EE2B2A] text-[18px] font-bold uppercase tracking-wider select-none pr-0 relative left-[8px]">
              Beta
            </span>
          )}
        </div>
      </div>
    );
  }
);
export default AppRightSection;
